import classNames from 'classnames';
import {type Editor} from '@tiptap/core';
import pick from '@tinkoff/utils/object/pick';
import {CodeBlock, type CodeBlockOptions} from '@tiptap/extension-code-block';
import {NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer} from '@tiptap/react';

import * as SyntaxHighlighting from 'utils/syntax_highlighting';
import {lowlight} from 'lowlight';
import highlight from 'highlight.js/lib/core';

import {lowlightPlugin} from './lowlight-plugin';

import styles from './styles.module.css';

type CodeBlockComponentProps = {
    editor: Editor;
    node: {
        attrs: {
            language?: string;
        };
    };
    extension: {
        options: CodeBlockOptions;
    };
};

const CodeBlockComponent = (props: CodeBlockComponentProps) => {
    const exitOnTripleEnter = props.extension.options.exitOnTripleEnter;
    const blockSeparator = exitOnTripleEnter ? '\n\n\n' : '\n\n';

    let text = props.editor.getText({blockSeparator});

    if (text.endsWith('\n\n\n') && exitOnTripleEnter) {
        text = text.substring(0, text.length - 3);
    }

    const selectedLanguage = highlight.getLanguage(props.node.attrs.language || '')?.name?.toLowerCase();

    return (
        <NodeViewWrapper className={styles.codeBlockWraper}>
            {selectedLanguage && (
                <div className={styles.lineNumbers}>
                    {SyntaxHighlighting.generateLineNumbers(text).map((number) => (
                        <span key={number}>{number}</span>
                    ))}
                </div>
            )}
            <pre className={classNames(styles.codeBlock, {[styles.langDetected]: selectedLanguage})}>
                <NodeViewContent
                    data-language={selectedLanguage}
                    style={{whiteSpace: 'pre'}}
                    className={styles.code}
                    as='code'
                />
            </pre>
        </NodeViewWrapper>
    );
};

// additional aliases
lowlight.registerAlias('javascript', ['javascriptreact']);
lowlight.registerAlias('typescript', ['typescriptreact']);

export const TimeWebkitCodeBlockExtension = CodeBlock.extend<CodeBlockOptions & {ctrlSend: boolean}>({
    addOptions() {
        return {
            ...this.parent?.(),
            ctrlSend: false,
        };
    },

    addNodeView() {
        // eslint-disable-next-line new-cap
        return ReactNodeViewRenderer(CodeBlockComponent, {className: styles.codeBlockRenderer});
    },

    addAttributes() {
        return {
            language: {
                default: null,
                parseHTML: (element) => {
                    const {languageClassPrefix} = this.options;
                    const firstEl = element.firstElementChild as HTMLElement | null;

                    if (!firstEl) {
                        return null;
                    }

                    if (firstEl.dataset?.language) {
                        return firstEl.dataset.language;
                    }

                    const langClass = [...firstEl.classList].find((cls) => cls.startsWith(languageClassPrefix));

                    if (langClass) {
                        return langClass.replace(languageClassPrefix, '');
                    }

                    return null;
                },
                rendered: false,
            },
        };
    },

    renderHTML({node, HTMLAttributes}) {
        return [
            'pre',
            {...HTMLAttributes, class: classNames(HTMLAttributes.class, styles.codeBlock)},
            [
                'code',
                {
                    'data-language': node.attrs.language ? node.attrs.language : undefined,
                },
                0,
            ],
        ];
    },

    addKeyboardShortcuts() {
        const emptyCommand = () => false;
        const ctrlSend = this.options.ctrlSend;
        const parentShortcuts = this.parent?.() || {};
        const handleEnter = pick<string, typeof parentShortcuts>(['Enter'], parentShortcuts).Enter || emptyCommand;

        return {
            ...pick(['Backspace', 'ArrowDown'], parentShortcuts),

            'Mod-Alt-Shift-c': () => {
                if (this.editor.isActive('table')) {
                    return false;
                }
                return this.editor.commands.toggleCodeBlock();
            },

            Enter: (params) => {
                if (!ctrlSend) {
                    return false;
                }

                return handleEnter(params);
            },

            'Shift-Enter': (params) => {
                return handleEnter(params);
            },

            'Mod-Enter': (params) => {
                if (ctrlSend) {
                    return false;
                }

                return handleEnter(params);
            },
        };
    },

    addInputRules() {
        const parentInputRules = this.parent?.() || [];

        return parentInputRules.map((inputRule) => ({
            ...inputRule,
            handler: (props) => {
                if (this.editor.isActive('table')) {
                    return;
                }
                return inputRule.handler(props);
            },
        }));
    },

    addProseMirrorPlugins() {
        const parentProseMirrorPlugins = this.parent?.() || [];

        return [
            ...parentProseMirrorPlugins,
            lowlightPlugin({
                name: this.name,
                lowlight,
            }),
        ];
    },
});
